import { DropdownItem } from '@frk/eds-components';
import { ToggleOption } from '@types';
import { SeriesOptionsType } from 'highcharts';
import { GermanCalculatorResult } from './model/german-calculator';
import { PensionResult } from './model/pension-calculator';

export interface GermanCalculatorState {
  data?: GermanCalculatorResult;
  config?: GermanCalculatorConfig;
  pensionConfig?: GermanCalculatorPensionConfig;
  pensionResult?: PensionResult;
  hasLoadedFullFeed?: boolean;
}

export interface GermanCalculatorConfig {
  formValues: GermanCalculatorFormValues;
  defaultResultType: GermanCalculatorResultType;
  summaryData?: GermanCalculatorSummaryData[];
  chartData?: GermanCalculatorChartData;
  tableData?: GermanCalculatorTableData;
}

export interface GermanCalculatorFormValues {
  calculationTypeItems: DropdownItem[];
  bindValues: GermanCalculatorFormBindValues;
  maxValues: GermanCalculatorFormMaxValues;
  maxLabelValues: GermanCalculatorFormMaxLabelValues;
  invalidLabelValues: GermanCalculatorFormInvalidLabelValues;
  installmentPeriodItems?: DropdownItem[];
  perpetualAnnuityItems?: DropdownItem[];
  payoutItems?: DropdownItem[];
}

export interface GermanCalculatorFormBindValues {
  defaultCalculationType?: GermanCalculationType;
  initialContribution?: string;
  finalCapital?: string;
  initialSalesCharge?: string;
  investmentPeriod?: string;
  interestRate?: string;
  savingsRate?: string;
  defaultInstallmentPeriod?: GermanCalculatorInstallmentPeriod;
  pension?: string;
  defaultPerpetualAnnuity?: GermanCalculatorPerpetualAnnuity;
  defaultPayout?: GermanCalculatorInstallmentPeriod;
}

export interface GermanCalculatorFormMaxValues {
  maxInitialContribution?: string;
  maxFinalCapital?: string;
  maxSalesCharge?: string;
  maxInvestmentPeriod?: string;
  maxInterestRate?: string;
  maxSavingsRate?: string;
  maxPension?: string;
}

export interface GermanCalculatorFormMaxLabelValues {
  maxInitialContributionLabel?: string;
  maxFinalCapitalLabel?: string;
  maxSavingsRateLabel?: string;
  maxPensionLabel?: string;
}

export interface GermanCalculatorFormInvalidLabelValues {
  initialSalesChargeInvalidText?: string;
  interestRateInvalidText?: string;
  noResultErrorMessage?: string;
  apiErrorMessage?: string;
}

export interface GermanCalculatorAPIRequestParams {
  calculationType?: string;
  initialBalance?: string;
  finalBalance?: string;
  initialSalesCharge?: string;
  durationYears?: string;
  durationDays?: string;
  interestRate?: string;
  instalmentAmount?: string;
  instalmentFrequency?: string;
  withdrawalFrequency?: string;
  perpetuity?: string;
  withdrawalAmount?: string;
}

export interface GermanCalculatorSummaryData {
  label: string;
  value: string;
  valueType: GermanCalculatorSummaryDataValueType;
  showLegend: boolean;
  legendColor?: string;
  currencySymbol?: string;
}

export enum GermanCalculatorSummaryDataValueType {
  'PERIOD' = 'PERIOD',
  'RATE' = 'RATE',
  'AMOUNT' = 'AMOUNT',
}

export interface GermanCalculatorChartData {
  chartTitle: string;
  proximalPlacementTop: string;
  proximalPlacementBottom: string;
  caveatPlacement: string;
  tooltip: string;
  series: SeriesOptionsType[];
  xAxisCategories: string[];
  xAxisLabel: string;
  yAxisLabel: string;
}

export interface GermanCalculatorTableData {
  headers: string[];
  body: string[][];
}

export interface GermanCalculatorTotalPages {
  count: number;
}

export enum GermanCalculatorType {
  'ONE_TIME_INVESTMENT',
  'SAVINGS_PLAN',
  'WITHDRAWL_PLAN',
  'PENSION',
  'PENSION_ESTIMATOR',
  'SHORTFALL_CLOSURE',
}

export enum GermanCalculationType {
  'INITIAL_CAPITAL' = 'INITIAL_CAPITAL',
  'SAVINGS_RATE' = 'INSTALMENT_AMOUNT',
  'TERM' = 'TERM',
  'INTEREST_RATE' = 'INTEREST_RATE',
  'END_VALUE' = 'END_VALUE',
  'PENSION' = 'INSTALMENT_AMOUNT',
}

export enum GermanCalculatorResultType {
  'CHART',
  'TABLE',
}

export type GermanCalculatorBRConfig = GermanCalculatorFormBindValues &
  GermanCalculatorFormMaxValues & {
    defaultResultType: GermanCalculatorResultType;
  };

export enum GermanCalculatorInstallmentPeriod {
  'YEARLY' = 'YEARLY',
  'HALF_YEARLY' = 'HALF_YEARLY',
  'QUARTERLY' = 'QUARTERLY',
  'BI_MONTHLY' = 'BI_MONTHLY',
  'MONTHLY' = 'MONTHLY',
}

export enum GermanCalculatorPerpetualAnnuity {
  'YES' = 'YES',
  'NO' = 'NO',
}

// German Pension Calculator Types

export interface GermanPensionCalulatorFormValues {
  calculationTypeItems?: DropdownItem[];
  bindValues?: GermanPensionCalculatorFormBindValues;
  maxValues?: GermanPensionCalculatorFormMaxValues;
  maxLabelValues?: GermanPensionCalculatorFormMaxLabelValues;
  minValues?: GermanPensionCalculatorFormMinValues;
  invalidLabelValues?: GermanPensionCalculatorFormInvalidLabelValues;
  retirementAgeYears?: DropdownItem[];
  retirementAgeMonths?: DropdownItem[];
  genderToggle?: ToggleOption[];
  knowEarningPointToggle?: ToggleOption[];
  employedInEastToggle?: ToggleOption[];
  pointsSectionEmployedInEastToggle?: ToggleOption[];
  pensionAdjustmentToggle?: ToggleOption[];
  inflationAdjustmentToggle?: ToggleOption[];
  annualPensionAdjustmentToggle?: ToggleOption[];
}

export interface GermanPensionCalculatorFormBindValues {
  // required values
  eventName?: PensionCalculatorEventType;

  // step 1
  currencyCode?: string;
  birthDate?: string;
  gender?: GermanPensionCalculatorGender;
  pointsSectionEmployedInEast?: string;
  accountClearing?: number;
  employmentStartYear?: string;
  grossIncomeInitial?: string;
  grossIncomeCurrent?: string;
  retirementAgeYears?: string;
  retirementAgeMonths?: string;
  pointsDate?: string;
  pointsEast?: string;
  pointsWest?: string;
  employedInEast?: GermanPensionCalculatorEmployedInEast; // employment relate to old or new federal states
  knowPayPoints?: GermanPensionCalculatorKnowPayPoints;
  lastAccountClearing?: string;

  // step 2
  pensionAdjustments?: GermanPensionAdjustments;
  statutoryPensionEntitlement?: string;
  otherIncome?: string;
  incomeOldage?: string;
  expectedExpenses?: string;
  considerInflation?: string;
  spendingOldage?: string;

  pensionEntitlement?: string;
  addtionalIncome?: string;
  pensionGap?: string;

  // Step 3
  annualPensionAdjustment?: string;
  supplementaryPension?: string;
  paymentDuration?: string;
  assumedPerformance?: string;
  savingsPeriod?: string;
  savingInterestRate?: string;
  savingBalance?: string;
}

export interface GermanPensionCalculatorFormMaxValues {
  birthDate?: string;
  lastAccountClearing?: string;
}

export interface GermanPensionCalculatorFormMinValues {
  birthDate?: string;
  lastAccountClearing?: string;
  maxPayPoints?: string;
}

export interface GermanPensionCalculatorFormMaxLabelValues {
  maxPayPointsLabel?: string;
}

export interface GermanPensionCalculatorFormInvalidLabelValues {
  grossIncomeCurrent?: string;
  pointsEast?: string;
  pointsWest?: string;
}

export interface GermanPensionCalculatorFormRequiredLabelValues {
  birthDate?: string;
  grossIncomeCurrent?: string;
  pointsEast?: string;
  pointsWest?: string;
  lastAccountClearing?: string;
}

export enum GermanPensionCalculatorEmployedInEast {
  'OLD' = '1',
  'NEW' = '2',
}

export enum GermanPensionCalculatorKnowPayPoints {
  'YES' = '1',
  'NO' = '2',
}

export enum GermanPensionCalculatorConsiderInflation {
  'YES' = '1',
  'NO' = '0',
}

export enum GermanPensionCalculatorAnnualPensionAdjustment {
  'YES' = '1',
  'NO' = '0',
}

export enum GermanPensionCalculatorGender {
  'MALE' = '1',
  'FEMALE' = '2',
}

export enum GermanPensionAdjustments {
  'NONE',
  'PERCENT_1',
  'PERCENT_2',
}

export interface GermanCalculatorPensionConfig {
  steps: string[];
  currentStep: number;
  formValues: GermanPensionCalulatorFormValues;
  /**
   * To-Do:
   * Form values - bind, max, label values
   * Results values
   */
}

export enum PensionCalculatorEventType {
  'PENSION_STEP1_POPULATE_POINTS',
  'PENSION_STEP1_POPULATE_INCOME',
  'PENSION_CALC_GOTO_STEP_1',
  'PENSION_CALC_GOTO_STEP_2',
  'PENSION_CALC_GOTO_STEP_3',
  'PENSION_CALC_GOTO_STEP_4',
  'PENSION_CALC_RECALCULATE',
  'PENSION_STEP2_CALCULATE',
  'PENSION_STEP2_CALCULATE_ADDITIONAL_PENSION',
  'PENSION_STEP3_CALCULATE_SHORTFALL_CLOSURE',
  'PENSION_START_NEW_CALCULATION',
}

export interface GermanCalculatorPensionCalculateEvent
  extends GermanPensionCalculatorFormBindValues {
  currentStep?: number;
  eventName?: PensionCalculatorEventType;
  shortfallAmount?: string;
  shortfallDurationYears?: string;
  shortfallPerpetuity?: string;
  shortfallInterestRate?: string;
  savingBalance?: string;
  savingDurationYears?: string;
  savingInterestRate?: string;
}
